<template>
  <ChatLayout>
    <template #content>

      <div v-if="loading" class="d-flex align-center justify-center" style="height:100%">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <!-- Phase: no project yet — centered input -->
      <div
        v-else-if="!status"
        class="chat-input pa-md-4 d-flex align-center no-messages"
      >
        <ChatInput @send="onQuery" :disabled="sending" />
      </div>

      <!-- Phase: questioning / ready — questions + brief button -->
      <template v-else-if="status === 'questioning' || status === 'ready'">
        <!-- Loader: visible until first question streams in -->
        <div v-if="questions.length === 0" class="centered-state d-flex flex-column align-center justify-center">
          <div class="logo-pulse">
            <v-img :src="$theme.assets.logoContent" width="120" contain class="mb-3" />
          </div>
          <div class="thinking mb-3">
            <span class="dot secondary"></span>
            <span class="dot secondary"></span>
            <span class="dot secondary"></span>
          </div>
        <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[0] }">Analyse en cours...</p>
        <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[1] }">Détection du mode d'interaction...</p>
        <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[2] }">Qualification du domaine métier...</p>
        <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[3] }">Analyse des contraintes</p>
        <p class="mb-0 text-body-2 grey--text fade-step" :style="{ animationDelay: stepDelays[4] }">Génération des questions</p>
        </div>

        <div v-else class="messages overflow-y-auto pa-md-4 pa-2" ref="messagesContainer">
          <div class="mb-4">
            <v-chip small color="primary" outlined class="mr-2">
              <v-icon left small>mdi-domain</v-icon>
              {{ domain }}
            </v-chip>
            <v-chip v-if="initRouting" small color="secondary" outlined>
              <v-icon left x-small>mdi-tag-outline</v-icon>
              Devis quantitatif
            </v-chip>
          </div>

          <div v-if="initialMessage" class="user-prompt mb-6 pa-4">
            <div class="user-prompt__label mb-1">Vous avez demandé</div>
            <div class="user-prompt__body" :class="{ 'is-typing': isTyping }">
              <span class="user-prompt__quote">&ldquo;</span>{{ typedMessage }}<span class="user-prompt__quote">&rdquo;</span>
            </div>
          </div>

          <QuestionForm
            :questions="questions"
            :answers="answers"
            :questions-ready="questionsReady"
            @answer="onAnswer"
            class="mb-6"
          />

          <v-btn
            v-if="questionsReady"
            color="primary"
            :disabled="!canGenerateBrief"
            :loading="sending"
            @click="onGenerateBrief"
            elevation="0"
          >
            <v-icon left>mdi-text-box-check-outline</v-icon>
            Générer le brief
          </v-btn>
        </div>
      </template>

      <!-- Phase: generating / complete — streaming brief -->
      <template v-else-if="status === 'generating' || status === 'complete'">
        <div
          class="messages overflow-y-auto pa-md-4 pa-2"
          ref="messagesContainer"
          :class="{ 'scrolled': isScrolled }"
        >
          <v-btn
            @click="animateScrollToTop()"
            fab
            fixed
            absolute
            right
            bottom
            v-if="isScrolled"
            elevation="1"
          >
            <v-icon>mdi-arrow-up</v-icon>
          </v-btn>

          <ProjectBrief
            :brief="brief"
            :brief-raw="briefRaw"
            :status="status"
            :project-id="currentProjectId"
            :domain="domain"
            :init-routing="initRouting"
            :session-id="agentSessionId"
          />
        </div>
      </template>

    </template>
  </ChatLayout>
</template>

<script>
import ChatLayout from '@/layout/ChatLayout.vue'
import ChatInput from '@/components/chat/ChatInput.vue'
import QuestionForm from '@/components/QuestionForm.vue'
import ProjectBrief from '@/components/chat/ProjectBrief.vue'

export default {
  name: 'ProjectView',
  components: { ChatLayout, ChatInput, QuestionForm, ProjectBrief },
  data() {
    return {
      loading: false,
      isScrolled: false,
      typedMessage: '',
      isTyping: false,
      stepDelays: this._randomStepDelays(),
    }
  },
  computed: {
    currentProjectId() {
      return this.$route.params.id || null
    },
    status() { return this.$store.getters['project/status'] },
    questions() { return this.$store.getters['project/questions'] },
    answers() { return this.$store.getters['project/answers'] },
    brief() { return this.$store.getters['project/brief'] },
    domain() { return this.$store.getters['project/domain'] },
    agentSessionId() { return this.$store.getters['project/agentSessionId'] },
    canGenerateBrief() { return this.$store.getters['project/canGenerateBrief'] },
    initialMessage() { return this.$store.getters['project/initialMessage'] },
    sending() { return this.$store.state.project.sending },
    initRouting() { return this.$store.state.chat.initRouting },
    questionsReady() { return !!this.domain },
    briefRaw() { const b = this.$store.state.project.currentProject?.brief; return typeof b === 'string' ? b : '' },
  },
  watch: {
    currentProjectId(newId) {
      this.$store.commit('chat/CLEAR_INIT_ROUTING')
      if (newId && newId !== this.$store.state.project.currentProject?.id) {
        this.loading = true
        this.$store.dispatch('project/loadProject', newId).finally(() => { this.loading = false })
      } else if (!newId) {
        this.$store.commit('project/CLEAR_PROJECT')
      }
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(300)
        }, 100)
      })
    },
    brief() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(200)
        }, 50)
      })
    },
    questions() {
      this.$nextTick(() => this.animateScrollToBottom(100))
    },
    initialMessage: {
      immediate: true,
      handler(val) {
        if (val) this.startTyping(val)
      },
    },
  },
  methods: {
    _randomStepDelays() {
      let acc = 0
      return Array.from({ length: 5 }, () => {
        acc += Math.random() * 2 + 1
        return `${acc.toFixed(2)}s`
      })
    },
    startTyping(text) {
      clearTimeout(this._typingTimer)
      this.typedMessage = ''
      this.isTyping = true
      let i = 0
      const tick = () => {
        if (i < text.length) {
          this.typedMessage += text[i++]
          this._typingTimer = setTimeout(tick, 80)
        } else {
          this.isTyping = false
        }
      }
      this._typingTimer = setTimeout(tick, 80)
    },
    onQuery(text) {
      if (!text) return
      this.$store.dispatch('project/submitQuery', text)
    },
    onAnswer(key, value) {
      this.$store.dispatch('project/updateAnswer', { key, value })
    },
    onGenerateBrief() {
      this.$store.dispatch('project/generateBrief')
    },
    animateScrollToBottom(duration = 300) {
      const el = this.$refs.messagesContainer
      if (el) {
        this.isScrolled = true
        this.$vuetify.goTo(el.scrollHeight, { container: el, duration })
      }
    },
    animateScrollToTop(duration = 300) {
      const el = this.$refs.messagesContainer
      if (el) {
        this.$vuetify.goTo(0, { container: el, duration })
        this.isScrolled = false
      }
    },
    handleScroll() {
      const el = this.$refs.messagesContainer
      if (el) this.isScrolled = el.scrollTop > 0
    },
    setupScrollListener() {
      const el = this.$refs.messagesContainer
      if (el) el.addEventListener('scroll', this.handleScroll)
    },
    removeScrollListener() {
      const el = this.$refs.messagesContainer
      if (el) el.removeEventListener('scroll', this.handleScroll)
    },
  },
  async mounted() {
    if (this.currentProjectId) {
      // Skip loadProject if we navigated here from NewView and already have
      // the project shell in the store (questions are streaming in via SSE).
      if (this.$store.state.project.currentProject?.id === this.currentProjectId) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.setupScrollListener()
            this.animateScrollToBottom(300)
          }, 200)
        })
        return
      }
      this.loading = true
      await this.$store.dispatch('project/loadProject', this.currentProjectId)
      this.loading = false
      this.$nextTick(() => {
        setTimeout(() => {
          this.setupScrollListener()
          this.animateScrollToBottom(300)
        }, 200)
      })
    }
  },
  beforeDestroy() {
    this.removeScrollListener()
    clearTimeout(this._typingTimer)
  },
}
</script>

<style lang="scss" scoped>
.v-btn--fab.v-size--default.v-btn--absolute.v-btn--bottom {
  bottom: 16px;
}

.centered-state {
  height: 100%;
}

.thinking {
  display: flex;
  gap: 4px;
}

.dot {
  width: 8px;
  height: 8px;
  background: var(--app-loading-dot, #bbb);
  border-radius: 50%;
  animation: thinking-blink 1.2s infinite ease-in-out;
}

.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes thinking-blink {
  0%, 80%, 100% { opacity: 0.2; }
  40% { opacity: 1; }
}

.logo-pulse {
  animation: scale-pulse 2s infinite ease-in-out;
}

@keyframes scale-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

.fade-step {
  opacity: 0;
  animation: fade-in 0.6s ease forwards;
}

@keyframes fade-in {
  to { opacity: 1; }
}

.chat-input {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  &.no-messages {
    height: 100%;
    justify-self: center;
  }
}

.user-prompt {
  background: var(--app-prompt-bg, rgba(0, 0, 0, 0.03));
  border-left: 3px solid var(--v-primary-base);
  border-radius: 4px;

  &__label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--app-prompt-label, var(--v-primary-base));
    opacity: 0.8;
  }

  &__body {
    font-size: 0.95rem;
    color: var(--app-prompt-text, rgba(0, 0, 0, 0.75));
    font-style: italic;
    line-height: 1.5;

    &.is-typing::after {
      content: '|';
      margin-left: 1px;
      opacity: 0.6;
      animation: cursor-blink 0.6s step-end infinite;
    }
  }

  &__quote {
    font-size: 1.2em;
    font-style: normal;
    opacity: 0.4;
    margin: 0 2px;
  }
}

@keyframes cursor-blink {
  0%, 100% { opacity: 0.6; }
  50%       { opacity: 0; }
}

.messages.scrolled {
  box-shadow: inset 0 4px 6px -1px rgba(0, 0, 0, 0);
}
</style>
